import _ from 'lodash';

import {
  customSequenceTypes,
  dimensionSequences,
  dimensionTypes,
  getDefaultSequence,
  getDefaultSequenceDimensions,
} from '../../constants/retention';
import { createFilterReducer } from '../filter';
import {
  retentionEventFilterActions,
  retentionEventFilterActionsPrefix,
} from '../../constants/filter';
import { fromFilter } from '../../converters/filterConverter';
import { RETENTION_TEMPLATE_TYPE } from '../../constants/dashboards';
import { excludeEventSpecificClauses } from '../../utils/filter';

const eventFilterReducer = createFilterReducer(retentionEventFilterActionsPrefix);

const defaultFilter = eventFilterReducer(undefined, {});

const getFilterFromTemplate = (eventName, eventFilter, filterExtractor) =>
  eventName !== null
    ? fromFilter(eventFilter, (field) => filterExtractor(eventName, field))
    : defaultFilter;

const composeInitialState = {
  firstEvent: null,
  returnEvent: null,
  useLatestProfile: false,
  dimensionType: dimensionTypes.DAILY,
  filters: [defaultFilter, defaultFilter],
  customSequenceEnabled: false,
  customSequenceType: customSequenceTypes.MANUAL,
  customSequence: null,
  customSequenceDimensions: null,
  idUsingTemplate: undefined,
  sql: '',
  error: '',
};

const compose = (state = composeInitialState, action) => {
  switch (action.type) {
    case 'CHANGE_RETENTION_COMPLEX_FIRST_EVENT': {
      const changedFilterFirst = excludeEventSpecificClauses(
        state.filters[0],
        state.firstEvent || '',
        action.event,
        action.eventMetainf
      );
      return {
        ...state,
        firstEvent: action.event,
        filters: [changedFilterFirst, state.filters[1]],
      };
    }

    case 'CHANGE_RETENTION_COMPLEX_RETURN_EVENT': {
      const changedFilterReturn = excludeEventSpecificClauses(
        state.filters[1],
        state.returnEvent || '',
        action.event,
        action.eventMetainf
      );
      return {
        ...state,
        returnEvent: action.event,
        filters: [state.filters[0], changedFilterReturn],
      };
    }

    case 'CHANGE_RETENTION_COMPLEX_DIMENSION_TYPE':
      return {
        ...state,
        dimensionType: action.dimensionType,
        customSequence: getDefaultSequence(state.customSequenceType, action.dimensionType),
        customSequenceDimensions: getDefaultSequenceDimensions(
          state.customSequenceType,
          action.dimensionType
        ),
      };

    case 'EXPOSE_RETENTION_COMPLEX_QUERY_SUCCESS':
      return {
        ...state,
        error: '',
        sql: action.sql,
      };

    case 'SET_RETENTION_COMPLEX_EXPOSURE_ERROR':
      return {
        ...state,
        sql: '',
        error: action.error,
      };

    case 'TOGGLE_RETENTION_COMPLEX_CUSTOM_SEQUENCE':
      return {
        ...state,
        customSequenceEnabled: !state.customSequenceEnabled,
        customSequenceType: customSequenceTypes.MANUAL,
        customSequence: dimensionSequences[state.dimensionType],
      };

    case 'SET_RETENTION_COMPLEX_CUSTOM_SEQUENCE_TYPE':
      return {
        ...state,
        customSequenceType: action.customSequenceType,
        customSequence: dimensionSequences[state.dimensionType],
        customSequenceDimensions: {
          from: state?.customSequence.at(0) || 0,
          to: state?.customSequence.at(-1) || 0,
        },
      };

    case 'CHANGE_RETENTION_COMPLEX_CUSTOM_SEQUENCE':
      return {
        ...state,
        customSequence: action.customSequence,
      };

    case 'SET_RETENTION_COMPLEX_CUSTOM_SEQUENCE_FROM':
      return {
        ...state,
        customSequenceDimensions: {
          ...state.customSequenceDimensions,
          from: action.from,
        },
      };

    case 'SET_RETENTION_COMPLEX_CUSTOM_SEQUENCE_TO':
      return {
        ...state,
        customSequenceDimensions: {
          ...state.customSequenceDimensions,
          to: action.to,
        },
      };

    case 'CALCULATE_RETENTION_COMPLEX_CUSTOM_SEQUENCE':
      return {
        ...state,
        customSequence: _.range(
          state.customSequenceDimensions.from,
          state.customSequenceDimensions.to + 1
        ),
        sql: '',
      };

    case retentionEventFilterActions.ADD_FILTER_CLAUSE:
    case retentionEventFilterActions.COPY_FILTER:
    case retentionEventFilterActions.REMOVE_FILTER:
    case retentionEventFilterActions.REMOVE_FILTER_CLAUSE:
    case retentionEventFilterActions.SET_FILTER_CLAUSE_FIELD:
    case retentionEventFilterActions.SET_FILTER_CLAUSE_VALUE:
    case retentionEventFilterActions.SET_CLAUSES_CONCAT_OPERATOR:
    case retentionEventFilterActions.SET_FILTER_CLAUSE_OPERATOR:
    case retentionEventFilterActions.SET_FILTER_VISIBILITY:
      return {
        ...state,
        filters: [
          ...state.filters.slice(0, action.eventIndex),
          eventFilterReducer(state.filters[action.eventIndex], action),
          ...state.filters.slice(action.eventIndex + 1),
        ],
      };

    case `CREATE_TEMPLATE_${RETENTION_TEMPLATE_TYPE}_SUCCESS`:
    case `SET_TEMPLATE_${RETENTION_TEMPLATE_TYPE}`: {
      // составление массива фильтров на firstEvent и returnEvent
      const arrFilters = [
        getFilterFromTemplate(
          _.get(action, 'template.retentionQuery.query.firstEvent', null),
          _.get(action, 'template.retentionQuery.query.firstEventFilter', null),
          action.template.filterExtractor
        ),
        getFilterFromTemplate(
          _.get(action, 'template.retentionQuery.query.returnEvent', null),
          _.get(action, 'template.retentionQuery.query.returnEventFilter', null),
          action.template.filterExtractor
        ),
      ];
      return {
        ...state,
        idUsingTemplate: action.template.id,
        firstEvent: _.get(action, 'template.queries[0].query.firstEvent', state.firstEvent),
        returnEvent: _.get(action, 'template.queries[0].query.returnEvent', state.returnEvent),
        dimensionType: _.get(
          action,
          'template.queries[0].query.sequence.type',
          state.dimensionType
        ),
        customSequenceEnabled: true,
        customSequenceType: customSequenceTypes.MANUAL,
        customSequence: _.get(
          action,
          'template.queries[0].query.sequence.units',
          state.customSequence
        ),
        filters: arrFilters,
      };
    }

    case `RESET_TEMPLATE_${RETENTION_TEMPLATE_TYPE}`:
      return {
        ...state,
        idUsingTemplate: undefined,
      };

    case 'DELETE_TEMPLATE_SUCCESS':
      return {
        ...state,
        idUsingTemplate: state.idUsingTemplate === action.id ? undefined : state.idUsingTemplate,
      };

    default:
      return state;
  }
};

export default compose;

export const getFirstEvent = (state) => state.firstEvent;

export const getFirstEventFilter = (state) => state.filters[0];

export const getReturnEvent = (state) => state.returnEvent;

export const getReturnEventFilter = (state) => state.filters[1];

export const getUseLatestProfile = (state) => state.useLatestProfile;

export const getDimensionType = (state) => state.dimensionType;

export const getFilterFor = (state, eventIndex) => state.filters[eventIndex];

export const isCustomSequenceEnabled = (state) => state.customSequenceEnabled;

export const getCustomSequenceType = (state) => state.customSequenceType;

export const getCustomSequence = (state) => state.customSequence;

export const getCustomSequenceDimensions = (state) => state.customSequenceDimensions;

export const getIdUsingTemplate = (state) => state.idUsingTemplate;

export const getRetentionComplexSQL = (state) => state.sql;

export const getError = (state) => state.error;
